import { appDefaults, POSTGREST_PROXY_PREFIX } from '../../app.config'

/**
 * Zentrale, geprüfte Konfiguration.
 *
 * Zwei Quellen, klare Rangfolge: Die Standardwerte stehen eingecheckt in
 * `app.config.ts`, eine `.env` überschreibt sie einzeln. Damit läuft das
 * Projekt direkt nach dem Klonen, und wer abweichen will, überschreibt
 * genau den einen Wert statt alle abzuschreiben.
 *
 * Enthält ein Wert Unsinn, bricht die App beim Start mit einer klaren
 * Meldung ab und nicht später mit einem "undefined is not a function"
 * im Netzwerk-Tab.
 */

/**
 * Genau die Felder, die diese Anwendung aus der Umgebung liest.
 *
 * Bewusst ein eigener Typ statt Vites ImportMetaEnv: Der bringt Felder
 * mit, die wir nicht brauchen, und macht jeden Test unnötig sperrig.
 */
export interface RawEnv {
  readonly DEV: boolean
  readonly MODE: string
  readonly VITE_API_BASE_URL: string | undefined
  readonly VITE_POSTGREST_BASE_URL: string | undefined
  readonly VITE_USE_DEV_PROXY: string | undefined
  readonly VITE_OIDC_AUTHORITY: string | undefined
  readonly VITE_OIDC_CLIENT_ID: string | undefined
  readonly VITE_OIDC_SCOPE: string | undefined
}

export interface AppConfig {
  /** Basis-URL der Weltmaschine-API, ohne abschließenden Schrägstrich. */
  readonly apiBaseUrl: string
  /**
   * Die tatsächliche Backend-URL, auf die Aufrufe gehen.
   *
   * Im Dev-Modus mit Proxy ist `apiBaseUrl` leer (Browser spricht mit
   * dem eigenen Origin), die Aufrufe landen aber über den Vite-Proxy
   * trotzdem bei einer echten Backend-URL. Dieses Feld zeigt sie an.
   * Ohne Proxy ist es identisch mit `apiBaseUrl`.
   */
  readonly apiTargetUrl: string
  /** Lesbare Beschriftung für den Umgebungs-Indikator. */
  readonly apiTargetLabel: string
  /**
   * Basis-URL für PostgREST-Aufrufe, ohne abschließenden Schrägstrich.
   *
   * Im Dev-Modus mit Proxy steht hier `/db`, damit der Aufruf beim
   * eigenen Origin bleibt und der Vite-Proxy ihn weiterreicht. Der
   * Präfix wird dabei serverseitig abgeschnitten, PostgREST sieht also
   * unverändert `/tabelle`. Ohne Proxy steht hier die echte URL.
   */
  readonly postgrestBaseUrl: string
  /**
   * Die tatsächliche PostgREST-URL, auf die Aufrufe gehen.
   *
   * Immer die echte Adresse, auch wenn der Proxy dazwischen sitzt.
   * Gedacht für Anzeige und Fehlermeldungen.
   */
  readonly postgrestTargetUrl: string
  /** Realm-URL von Keycloak. */
  readonly oidcAuthority: string
  /** Client-ID in Keycloak. */
  readonly oidcClientId: string
  /** Angeforderte Scopes, durch Leerzeichen getrennt. */
  readonly oidcScope: string
  /** Läuft die App im Entwicklungsmodus? */
  readonly isDevelopment: boolean
}

class ConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConfigError'
  }
}

/**
 * Nimmt den Wert aus der Umgebung, sonst den Standardwert.
 *
 * Eine leere oder nur aus Leerzeichen bestehende Variable zählt als
 * "nicht gesetzt". Ein versehentliches `VITE_OIDC_CLIENT_ID=` in der
 * .env soll die App nicht mit einer leeren Client-ID starten lassen.
 */
function read(key: string, value: string | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? ''
  if (trimmed !== '') return trimmed

  if (fallback.trim() === '') {
    throw new ConfigError(
      `Für ${key} gibt es weder einen Wert in der Umgebung noch einen ` +
        `Standardwert in app.config.ts.`,
    )
  }
  return fallback
}

function readUrl(key: string, value: string | undefined, fallback: string): string {
  const raw = read(key, value, fallback)
  try {
    new URL(raw)
  } catch {
    throw new ConfigError(
      `${key} ist keine gültige URL: "${raw}". ` +
        `Der Wert kommt aus der .env oder aus app.config.ts.`,
    )
  }
  return raw.replace(/\/+$/, '')
}

/**
 * Welches Backend ohne expliziten Überschreiber?
 *
 * Rangfolge: `VITE_API_BASE_URL` > Mode-Logik. Ist nichts davon
 * gesetzt, gilt der Staging-Standard aus app.config.ts.
 *
 * Der Vite-Mode `production` ist der Standard für `vite build` und
 * schaltet auf die Produktions-URL. Jeder andere Mode (development,
 * staging, custom) bleibt auf Staging.
 */
function resolveApiTarget(env: RawEnv): string {
  if (env.VITE_API_BASE_URL !== undefined && env.VITE_API_BASE_URL.trim() !== '') {
    return env.VITE_API_BASE_URL.trim()
  }
  return env.MODE === 'production' ? appDefaults.productionApiBaseUrl : appDefaults.apiBaseUrl
}

/**
 * Baut die Konfiguration aus Standardwerten und Umgebungsvariablen.
 *
 * Im Entwicklungsmodus mit aktivem Proxy zeigt die API-Basis bewusst auf
 * den eigenen Origin. Der Vite-Server leitet dann serverseitig weiter,
 * wodurch CORS entfällt. Siehe vite.config.ts.
 */
export function buildConfig(env: RawEnv): AppConfig {
  const isDevelopment = env.DEV
  const useProxy = readProxyFlag(env.VITE_USE_DEV_PROXY)
  const resolvedTarget = resolveApiTarget(env)
  const apiTargetUrl = readUrl('VITE_API_BASE_URL', env.VITE_API_BASE_URL, resolvedTarget)
  const postgrestTargetUrl = readUrl(
    'VITE_POSTGREST_BASE_URL',
    env.VITE_POSTGREST_BASE_URL,
    appDefaults.postgrestBaseUrl,
  )

  return {
    apiBaseUrl: isDevelopment && useProxy ? '' : apiTargetUrl,
    apiTargetUrl,
    apiTargetLabel: buildTargetLabel(env.MODE, apiTargetUrl, isDevelopment && useProxy),
    /*
     * Anders als bei der API steht hier im Proxy-Fall nicht der leere String,
     * sondern das Präfix. Die Backend-Routen tragen ihr `/api` schon selbst,
     * PostgREST-Pfade sind dagegen nackte Tabellennamen wie `/products`. Ohne
     * Präfix landeten sie beim Vite-Server statt im Proxy.
     */
    postgrestBaseUrl: isDevelopment && useProxy ? POSTGREST_PROXY_PREFIX : postgrestTargetUrl,
    postgrestTargetUrl,
    oidcAuthority: readUrl(
      'VITE_OIDC_AUTHORITY',
      env.VITE_OIDC_AUTHORITY,
      appDefaults.oidcAuthority,
    ),
    oidcClientId: read('VITE_OIDC_CLIENT_ID', env.VITE_OIDC_CLIENT_ID, appDefaults.oidcClientId),
    oidcScope: read('VITE_OIDC_SCOPE', env.VITE_OIDC_SCOPE, appDefaults.oidcScope),
    isDevelopment,
  }
}

/**
 * Baut den lesbaren Umgebungsnamen für den Indikator.
 *
 * Produktion bekommt eine Warnfarbe, Staging eine neutrale. Der
 * Indikator zeigt den Modus, nicht die URL, damit die Zeile kurz bleibt.
 * Die URL gibt es als Tooltip.
 */
function buildTargetLabel(mode: string, url: string, isProxied: boolean): string {
  const suffix = isProxied ? ' (Proxy)' : ''
  const host = url.replace(/^https?:\/\//, '')
  switch (mode) {
    case 'production':
      return `Produktion – ${host}${suffix}`
    case 'staging':
      return `Staging – ${host}${suffix}`
    default:
      return `Entwicklung – ${host}${suffix}`
  }
}

/**
 * Der Proxy-Schalter, gelesen wie ein Schalter und nicht wie ein Text.
 *
 * Nur ein ausdrückliches "false" schaltet ihn aus. Ein Tippfehler wie
 * `VITE_USE_DEV_PROXY=flase` lässt ihn an, und das ist die sichere
 * Richtung: Der Proxy an ist im Zweifel funktionierend, der Proxy aus
 * scheitert an CORS.
 */
function readProxyFlag(value: string | undefined): boolean {
  const trimmed = value?.trim().toLowerCase() ?? ''
  if (trimmed === '') return appDefaults.useDevProxy
  return trimmed !== 'false'
}

export const config: AppConfig = buildConfig(import.meta.env)

export { ConfigError }
